<template>
    <div>
        <div :class="styles.FORM.FORM_GROUP">
            <label>Select Open Dental Data Type</label>
            <select :class="styles.FORM.FORM_CONTROL"
                    v-model="control.dataType">
                <option v-for="item in dataTypes"
                        :key="item"
                        :value="item"
                        v-text="dataTypeLabel(item)">
                </option>
            </select>

            <!-- Loading indicator while fetching data from Open Dental -->
            <div class="mt-5" v-if="loadingOptions && control.dataType">
                <div class="loading-indicator">
                    <i class="fa fa-spinner fa-spin mr-2"></i>
                    <span>Loading {{ control.dataType.toLowerCase() }}...</span>
                </div>
            </div>

            <!-- "DISEASES with Category and Checkbox Options": the category builder REPLACES
                 the flat options multiselect below (it keeps control.selectedOptions equal to
                 the union of the categories, so everything downstream is unchanged). -->
            <div class="mt-5" v-if="isCategorized && !loadingOptions">
                <DiseasesCategorizedConfig
                    :control="control"
                    :formData="formData"
                    :siteID="siteID"
                    :allOptions="radioOptions"
                    :loading="loadingOptions"
                />
            </div>

            <!-- Options multiselect for DISEASES, ALLERGIES, and MEDICATIONS -->
            <div class="mt-5" v-else-if="!loadingOptions && control.dataType && radioOptions.length > 0">
                <label>
                    <template v-if="control.dataType === 'MEDICATIONS'">
                        All medications loaded ({{ radioOptions.length }}). Deselect any you don't want to include.
                    </template>
                    <template v-else>
                        Select Options you'd like user to pick from or <a href="#" @click="selectAll(control)">Select All</a>
                    </template>
                </label>
                <multiselect
                v-model="control.selectedOptions"
                :options="radioOptions"
                :multiple="true"
                :taggable="false"
                :show-labels="false"
                label="text"
                track-by="text"
                aria-placeholder
                @select="update($event)"
                />
            </div>

            <!-- Which of the offered options should turn on the red ALERT highlight in the
                 submission view / PDF. Empty = "anything other than None". Only shown once the
                 author has ticked Highlight for Alert AND picked some options to offer. -->
            <div class="mt-5 alert-options-picker"
                 v-if="control.highlightForAlert && Array.isArray(control.selectedOptions) && control.selectedOptions.length > 0">
                <label>Highlight only when the patient selects any of:</label>
                <multiselect
                    v-model="control.alertOptions"
                    :options="control.selectedOptions"
                    :multiple="true"
                    :taggable="false"
                    :show-labels="false"
                    label="text"
                    track-by="value"
                    placeholder="Any selection other than None"
                />
                <small class="alert-options-hint">Leave this empty to highlight whenever the patient selects anything other than None. Selecting None alone never triggers the highlight.</small>
            </div>
        </div>
    </div>
</template>

<script>
    import {CONTROL_SPECIAL_CONFIG_MIXIN} from "../../mixins/control-special-config-mixin";
    import {DROPDOWN_DATA_MODES} from "../../configs/control-config-enum";
    import ListItem from "../../libraries/list-item.class";
    import Multiselect from "vue-multiselect";
    import DiseasesCategorizedConfig from "./DiseasesCategorizedConfig";
    import {
        dataTypeLabel,
        isCategorizedDiseases,
        requestDataTypeFor
    } from "../../libraries/od-categorized-diseases";

    export default {
        name: "OpenDentalDataConfigView",
        props: {
            siteID: String
        },
        components: { Multiselect, DiseasesCategorizedConfig },
        mixins: [CONTROL_SPECIAL_CONFIG_MIXIN],
        data: () => ({
            dataTypes: ['DISEASES', 'ALLERGIES', 'MEDICATIONS', 'REFERRALS', 'DISEASES_CATEGORIZED'],
            apiURL: `${process.env.VUE_APP_TEAMIO_API_URL}/get-od-data`,
            radioOptions: [],
            selectedDataType: null,
            loadingOptions: false,
        }),

        watch: {
            'control.dataType'(newType) {
                if (newType && newType !== this.selectedDataType) {
                    this.selectedDataType = newType;
                    this.retrieveRadioOptionsList();
                }
            },
            value(val) {
                //console.log(`in opendentaldataconfigview control watch, value called with val: ${JSON.stringify(val)}`);
                this.setValue(val);
            },
            /**
             * Keep control.alertOptions a subset of the options actually offered to the patient.
             *
             * Removing an option from the offered list must also remove it from the trigger list,
             * otherwise the alert config goes silently inert (it can never match a submitted item).
             *
             * Safe against the wholesale rebuilds in selectAll() and the MEDICATIONS branch of
             * afterRestAPICallDataSuccessfully(): both empty and refill selectedOptions
             * SYNCHRONOUSLY, so the watcher fires once with the refilled array and prunes nothing.
             */
            'control.selectedOptions'(newOptions) {
                if (!Array.isArray(this.control.alertOptions) || !this.control.alertOptions.length) return;
                const offered = (Array.isArray(newOptions) ? newOptions : [])
                    .filter(o => o && typeof o === 'object')
                    .map(o => String(o.value));
                const pruned = this.control.alertOptions.filter(
                    o => o && typeof o === 'object' && offered.includes(String(o.value))
                );
                if (pruned.length !== this.control.alertOptions.length) {
                    this.control.alertOptions = pruned;
                }
            }
        },

        created() {
            // Vue 2 reactivity fix: ensure these properties exist reactively
            // on controls saved before configData included them
            if (!this.control.hasOwnProperty('dataType')) {
                this.$set(this.control, 'dataType', '');
            }
            if (!this.control.hasOwnProperty('selectedOptions')) {
                this.$set(this.control, 'selectedOptions', []);
            }
            // Which offered options trigger the red ALERT highlight. Empty/absent means
            // "anything other than None" — see src/pages/Forms/libraries/alert-highlight.js
            if (!this.control.hasOwnProperty('alertOptions')) {
                this.$set(this.control, 'alertOptions', []);
            }
            this.retrieveRadioOptionsList();
        },

        methods: {
            /**
             * Add new List-Item for the Current Radio/Checkbox
             */
            addListItem() {
                this.control.items.push(
                    new ListItem('', '')
                )
            },

            /**
             * Remove list-Item by Index of the Array
             */
            removeListItem(index) {
                this.control.items.splice(index, 1)
            },

            /**
             * The dropdown caption for a data-type key. Only DISEASES_CATEGORIZED has a
             * friendly label ("DISEASES with Category and Checkbox Options"); the four legacy
             * keys show exactly as before.
             */
            dataTypeLabel,

            update(e) {
                console.log(`update in OpenDentalDataConfigView called`);
                console.dir(e);
            },

            resetSelectedOptions() {
                this.control.selectedOptions = [];
            },

            retrieveRadioOptionsList() {
                if(this.control.dataType && this.siteID) {
                    this.loadingOptions = true;

                    let payload = {
                        // /get-od-data 400s on an unknown dataType, and it has never heard of
                        // DISEASES_CATEGORIZED — that key is a BUILDER concept over the same
                        // disease list, so it is translated back to 'DISEASES' for the fetch.
                        siteID: this.siteID,
                        dataType: requestDataTypeFor(this.control.dataType)
                    }

                    //console.log(`CheckboxAPI Payload`, payload);

                    fetch(this.apiURL, {
                        method: "POST",
                        body: JSON.stringify(payload)
                    }).then(function(result) {
                        console.log('result', result);
                        return result.json();
                    })
                    .then(this.afterRestAPICallDataSuccessfully)
                    .catch(this.restAPICallErrorHandling)
                    .finally(() => {
                        this.loadingOptions = false;
                    });
                }
            },

            afterRestAPICallDataSuccessfully(result) {
                //console.log(`in afterRest: result: ${JSON.stringify(result, null, 2)}`);
                if (!Array.isArray(result)) {
                    throw new TypeError(`[CHECKBOXAPI-${this.control.name}] Wrong API-data format.`);
                }

                if(!this.control.dataType || this.control.dataType === '') {
                    throw new TypeError(`[CHECKBOXAPI-${this.control.name}] Missing OD Data Type. Please reconfigure`);
                }

                // clear list
                this.radioOptions = [];                

                switch(this.control.dataType.toUpperCase()) {
                    case 'DISEASES' : this.textKey = 'DiseaseName'; this.valueKey = 'DiseaseDefNum'; break;
                    case 'ALLERGIES': this.textKey = 'Description'; this.valueKey = 'AllergyDefNum'; break;
                    case 'MEDICATIONS': this.textKey = 'MedName'; this.valueKey = 'MedicationNum'; break;
                    // Referral sources (OD `referral` table, HQ-scoped). Behaves like DISEASES:
                    // the list loads and the office curates it (or hits Select All). Deliberately
                    // NOT in the MEDICATIONS auto-select-all branch below — an office's referral
                    // list contains rows patients should never see.
                    case 'REFERRALS': this.textKey = 'ReferralName'; this.valueKey = 'ReferralNum'; break;
                    // The categorized diseases builder reads the SAME Open Dental disease list
                    // (the fetch above asked for DISEASES), so it takes the same keys.
                    // Deliberately NOT in the MEDICATIONS auto-select-all branch below: the
                    // office assigns diseases to categories itself.
                    case 'DISEASES_CATEGORIZED': this.textKey = 'DiseaseName'; this.valueKey = 'DiseaseDefNum'; break;
                }

                // Populate radioOptions for all data types
                result.forEach(optionObj => {
                    this.radioOptions.push(
                        new ListItem(
                            optionObj[this.valueKey],
                            optionObj[this.textKey]
                        )
                    );
                });

                // For MEDICATIONS, auto-select all options
                if(this.control.dataType.toUpperCase() === 'MEDICATIONS') {
                    this.control.selectedOptions = [];
                    // Copy all radioOptions to selectedOptions
                    this.radioOptions.forEach(option => {
                        this.control.selectedOptions.push({
                            text: option.text,
                            value: option.value
                        });
                    });
                }
            },

            restAPICallErrorHandling(e) {
                console.error(`[OpenDentalDataConfigView] API request failed.`, e);
            },

            selectAll(control) {
                control.selectedOptions = [];
                for (let option of this.radioOptions) {
                    control.selectedOptions.push({
                        text: option.text,
                        value: option.value
                    });
                }
            }
        },

        computed: {
            /**
             * Dropdown Data Modes List
             */
            listDataModes: () => DROPDOWN_DATA_MODES,

            /**
             * Is this control on the 5th data type? If so the category builder replaces the
             * flat options multiselect (and keeps selectedOptions as the union of categories).
             */
            isCategorized() {
                return isCategorizedDiseases(this.control);
            }
        }
    }
</script>

<style scoped>
.loading-indicator {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    color: #6c757d;
    font-size: 14px;
}

.loading-indicator i {
    color: #007bff;
    font-size: 16px;
}

.alert-options-hint {
    display: block;
    margin-top: 6px;
    color: #6c757d;
    font-size: 12px;
    line-height: 1.4;
}
</style>